import { AvailableVersions } from "@/domain/records/types";

/**
 * Domain Model for Report Metadata
 * Clean Architecture - Domain Layer
 */
export class ReportMetadata {
  constructor(
    public readonly recordId: string,
    public readonly availableVersions: AvailableVersions,
    public readonly maxVersion: number,
    public readonly availableReportTypes: string[],
    public readonly hasTranscript: boolean = false,
    public readonly transcriptVersions: number[] = [],
    public readonly qaVersions: number[] = [],
    public readonly personaVersions: number[] = [],
    public readonly jtbdVersions: number[] = [],
    public readonly customVersions: number[] = [],
    public readonly generalVersions: number[] = []
  ) {}

  /**
   * Calculate max version from all report types
   */
  static calculateMaxVersion(availableVersions: AvailableVersions): number {
    const allVersions: number[] = [];

    Object.values(availableVersions).forEach((versions) => {
      if (Array.isArray(versions)) {
        allVersions.push(...versions);
      }
    });

    return allVersions.length > 0 ? Math.max(...allVersions) : 0;
  }

  /**
   * Extract available report types from versions map
   */
  static extractReportTypes(availableVersions: AvailableVersions): string[] {
    return Object.keys(availableVersions).filter(
      (type) => availableVersions[type] && availableVersions[type].length > 0
    );
  }

  /**
   * Create ReportMetadata from available versions map
   */
  static fromAvailableVersions(
    recordId: string,
    availableVersions: AvailableVersions
  ): ReportMetadata {
    const reportTypes = this.extractReportTypes(availableVersions);
    const maxVersion = this.calculateMaxVersion(availableVersions);

    return new ReportMetadata(
      recordId,
      availableVersions,
      maxVersion,
      reportTypes,
      availableVersions.transcript?.length > 0,
      availableVersions.transcript || [],
      availableVersions.qa_report || [],
      availableVersions.persona_report || [],
      availableVersions.jtbd_markdown_report || [],
      availableVersions.custom_report || [],
      availableVersions.general || []
    );
  }

  /**
   * Get latest version for specific report type
   */
  getLatestVersion(reportType: string): number | null {
    const versions = this.availableVersions[reportType];
    return versions && versions.length > 0 ? Math.max(...versions) : null;
  }

  /**
   * Check if specific report type is available
   */
  hasReportType(reportType: string): boolean {
    return this.availableReportTypes.includes(reportType);
  }

  /**
   * Get version options for UI (Latest + numbered versions)
   */
  getVersionOptions(reportType: string): string[] {
    const versions = this.availableVersions[reportType] || [];
    if (versions.length === 0) return [];

    const sorted = [...versions].sort((a, b) => b - a);
    const options = [`Latest ${sorted[0]}`];

    for (let i = 1; i < sorted.length; i++) {
      options.push(`Version ${sorted[i]}`);
    }

    return options;
  }

  /**
   * Convert UI version option to actual version number
   */
  parseVersionOption(option: string, reportType: string): number | null {
    if (!option) return this.getLatestVersion(reportType);

    if (option === "Latest") {
      return this.getLatestVersion(reportType);
    }

    const version = parseInt(option.replace("Version ", ""), 10);
    return isNaN(version) ? this.getLatestVersion(reportType) : version;
  }
}
